import React, { memo } from 'react';
import { useSearchParams } from 'react-router-dom';
import ZabbixMap from './ZabbixMap';
import api from '../services/api';
import ListContextMenu, { HandleMenuContext, MenuContextProvider, useMenuContext } from './MenuContext';
import { Autocomplete, Backdrop, Box, Button, CircularProgress, FormControl, FormControlLabel, IconButton, MenuItem, OutlinedInput, Paper, Select, TextField, Tooltip } from '@mui/material';
import { grey } from '@mui/material/colors';
import { CachedOutlined, Info } from '@mui/icons-material';
import FormDialog from '../Empresa/FormDialog';
import HostItemsDialog from './HostItemsDialog';
import Draggable from 'react-draggable';
import theme from '../theme';

function extractItemKeys(mapData) {
  const keys = new Set();
  for (const link of mapData.links || []) {
    const matches = link.label.match(/{([^}]+)}/g) || [];
    for (const m of matches) {
      const inner = m.slice(1, -1);
      const parts = inner.split(':');
      if (parts.length >= 2) {
        const key = parts.slice(1).join(':').replace('.last(0)', '');
        keys.add(key);
      }
    }
  }
  return [...keys];
}

function App() {
  const [searchParams, setSearchParams] = useSearchParams();
  const paramEmpresa = searchParams.get('cod_empresa');
  const paramMapId = searchParams.get('map_id');
  const paramHostId = searchParams.get('hostid');

  const [map, setMap] = React.useState(false);
  const [imgs, setImgs] = React.useState([]);
  const [reload, setReload] = React.useState(false);
  const [id, setId] = React.useState(false);
  const [sysmapid, setSysmapid] = React.useState(null);
  const [maplist, setMaplist] = React.useState([]);
  const [hosts, setHosts] = React.useState([]);
  const [items, setItems] = React.useState([]);
  const [host, setHost] = React.useState(null);
  const [empresa, setEmpresa] = React.useState(null);
  const [empresas, setEmpresas] = React.useState([]);
  const [openBackDrop, setOpenBackDrop] = React.useState(false);
  const [problems, setProblems] = React.useState([]);
  const [handlerDialogEmpresa, setHandlerDialogEmpresa] = React.useState(false);
  const [hostItemsDialog, setHostItemsDialog] = React.useState({ open: false, hostid: null, hostname: '' });

  // Ref para controlar deep link ja processado
  const deepLinkProcessed = React.useRef(false);

  // Carrega lista de empresas
  React.useLayoutEffect(() => {
    api.get('map/all').then(res => {
      setEmpresas(res.data);
      document.title = "Horus - Diagramas";

      // Deep link: auto-seleciona empresa se parametro presente
      if (paramEmpresa && !deepLinkProcessed.current) {
        const found = res.data.find(e => e.cod_empresa == paramEmpresa);
        if (found) {
          setEmpresa(found);
        }
      }
    });
  }, []);

  // Deep link: quando empresa é selecionada via URL param, carrega mapas
  React.useEffect(() => {
    if (!empresa || deepLinkProcessed.current) return;
    if (!paramEmpresa || empresa.cod_empresa != paramEmpresa) return;

    api.get(`map/list/${empresa.cod_empresa}`).then(res => {
      setMaplist(res.data);

      // Auto-seleciona mapa se parametro presente
      if (paramMapId) {
        const foundMap = res.data.find(m => m.sysmapid == paramMapId);
        if (foundMap) {
          setSysmapid(foundMap);
          deepLinkProcessed.current = true;
          loadMap(empresa.cod_empresa, foundMap.sysmapid, foundMap.name);
        }
      }
    });
  }, [empresa]);

  // Polling de problems (Redis-backed)
  React.useEffect(() => {
    if (empresa == null) return;

    const fetchProblems = () => {
      api.get(`/dashboard/empresa/${empresa.cod_empresa}`)
        .then((res) => setProblems(res.data))
        .finally(() => setReload(false));
    };

    fetchProblems();
    const intervalId = setInterval(fetchProblems, 30000);
    return () => clearInterval(intervalId);
  }, [empresa, reload]);

  // WebSocket: atualiza alarmes do mapa em tempo real
  React.useEffect(() => {
    if (!empresa) return;
    const cod = empresa.cod_empresa;

    const handleNew = (event) => {
      const newAlarms = event.detail;
      if (!newAlarms || !Array.isArray(newAlarms)) return;
      const relevant = newAlarms.filter(a => a.cod_empresa == cod);
      if (relevant.length === 0) return;
      setProblems(prev => {
        const existing = new Set(prev.map(p => `${p.cod_empresa}:${p.event_id}`));
        const unique = relevant.filter(a => !existing.has(`${a.cod_empresa}:${a.event_id}`));
        return [...unique, ...prev];
      });
    };

    const handleRemoved = (event) => {
      const removed = event.detail;
      if (!removed || !Array.isArray(removed)) return;
      const removedSet = new Set(removed.map(a => `${a.cod_empresa}:${a.event_id}`));
      setProblems(prev => prev.filter(p => !removedSet.has(`${p.cod_empresa}:${p.event_id}`)));
    };

    window.addEventListener('alarms-new', handleNew);
    window.addEventListener('alarms-removed', handleRemoved);
    return () => {
      window.removeEventListener('alarms-new', handleNew);
      window.removeEventListener('alarms-removed', handleRemoved);
    };
  }, [empresa]);

  // Auto-refresh de items a cada 60s
  React.useEffect(() => {
    if (!map || !hosts.length || !empresa) return;

    const fetchItems = () => {
      const hostids = hosts.map(host => host[1]);
      const keys = extractItemKeys(map);

      if (keys.length > 0) {
        // Busca dinamica por keys dos links
        api.post('map/items-by-keys', {
          hosts: hostids,
          keys: keys,
          cod_empresa: empresa.cod_empresa
        }).then(res => {
          const mapped = res.data.map(item => ({
            key: `${item.hostname || item.hosts?.[0]?.host || ''}:${item.key_}.last(0)`,
            lastValue: item.lastvalue,
            itemid: item.itemid,
            title: `${item.hostname || item.hosts?.[0]?.host || ''} - ${item.key_
              .replace(/ifHCInOctets\.Virtual/g, "Entrada - ")
              .replace(/ifHCOutOctets\.Virtual/g, "Saída - ")
              .replace(/ifHCInOctets/g, "Entrada - ")
              .replace(/ifHCOutOctets/g, "Saída - ")
            }`
          }));
          setItems(mapped);
        });
      } else {
        // Fallback: busca items classicos (ifHCIn/Out)
        getItemsClassic();
      }
    };

    fetchItems();
    const interval = setInterval(fetchItems, 60000);
    return () => clearInterval(interval);
  }, [map, hosts, empresa]);

  // Deep link: centraliza no host se paramHostId presente
  React.useEffect(() => {
    if (!paramHostId || !hosts.length) return;
    const foundHost = hosts.find(h => h[1] == paramHostId);
    if (foundHost) {
      setHost(foundHost);
    }
  }, [hosts, paramHostId]);

  function getItemsClassic() {
    if (!hosts.length || !empresa) return;
    const hostids = hosts.map(host => host[1]);
    api.post('map/items', {
      hosts: hostids,
      cod_empresa: empresa.cod_empresa
    }).then(res => {
      const mapped = res.data.map(item => ({
        key: `${item.hosts[0].host}:${item.key_}.last(0)`,
        lastValue: item.lastvalue,
        itemid: item.itemid,
        title: `${item.hosts[0].host} - ${item.key_
          .replace(/ifHCInOctets\.Virtual/g, "Entrada - ")
          .replace(/ifHCOutOctets\.Virtual/g, "Saída - ")
          .replace(/ifHCInOctets/g, "Entrada - ")
          .replace(/ifHCOutOctets/g, "Saída - ")
        }`
      }));
      setItems(mapped);
    });
  }

  function loadMap(cod_empresa, mapId, mapName) {
    setMap([]);
    setHosts([]);
    setHost(null);
    setOpenBackDrop(true);

    api.get(`/map/get/${cod_empresa}/${mapId}`).then(res => {
      setImgs(res.data.imgs);
      setMap(res.data.map);
      setHosts(res.data.hosts);
      document.title = `${mapName} - Horus - Diagramas`;
      setOpenBackDrop(false);
    }).catch(() => setOpenBackDrop(false));
  }

  return (
    <>
      <Box sx={{ zIndex: 100, position: 'absolute', backgroundColor: 'rgb(0,0,0,0)' }}>
        <FormControl sx={{ m: 1, width: 200, mt: 3 }}>
          <Autocomplete
            sx={{ bgcolor: theme.palette.mode == "dark" ? grey[900] : grey[100] }}
            size="small"
            id="tags-outlined"
            options={empresas}
            onChange={(event, newValue) => {
              document.title = newValue ? ` ${newValue?.cod_empresa} - Horus - Diagramas` : "Horus - Diagramas";
              setEmpresa(newValue);
              setMaplist([]);
              setSysmapid(null);
              setHost(null);
              setHosts([]);
              if (newValue !== null) {
                api.get(`map/list/${newValue.cod_empresa}`).then(res => setMaplist(res.data));
              }
            }}
            key={'mapList'}
            isOptionEqualToValue={(option, value) => option.cod_empresa == value.cod_empresa}
            getOptionLabel={(option) => option.cod_empresa.toString()}
            value={empresa}
            filterSelectedOptions
            renderInput={(params) => (
              <TextField {...params} label="Empresa" />
            )}
          />
        </FormControl>
        <FormControl sx={{ m: 1, width: 300, mt: 3 }}>
          <Autocomplete
            sx={{ bgcolor: theme.palette.mode == "dark" ? grey[900] : grey[100] }}
            size="small"
            id="tags-outlined"
            options={maplist}
            onChange={(event, newValue) => {
              setSysmapid(newValue);
              setMap([]);
              setHosts([]);
              setHost(null);
              if (empresa === null || newValue === null) return;
              loadMap(empresa.cod_empresa, newValue.sysmapid, newValue.name);
            }}
            key={'mapSearch'}
            isOptionEqualToValue={(option, value) => option.sysmapid == value.sysmapid}
            getOptionLabel={(option) => option.name}
            value={sysmapid}
            filterSelectedOptions
            renderInput={(params) => (
              <TextField {...params} label="Mapa" />
            )}
          />
        </FormControl>
        <FormControl sx={{ m: 1, width: 350, mt: 3 }}>
          <Autocomplete
            sx={{ bgcolor: theme.palette.mode == "dark" ? grey[900] : grey[100] }}
            size="small"
            id="tags-outlined"
            options={hosts}
            onChange={(event, newValue) => setHost(newValue)}
            key={'hostSearch'}
            isOptionEqualToValue={(option, value) => option[1] == value[1]}
            getOptionLabel={(option) => option[2] + " " + option[3]}
            value={host}
            filterSelectedOptions
            renderInput={(params) => (
              <TextField {...params} label="Buscar host..." />
            )}
          />
        </FormControl>
        <FormControl sx={{ m: 1, mt: 3 }}>
          <Tooltip title={"Atualizar os problemas"}>
            <IconButton disabled={reload} variant='outlined' onClick={() => setReload(true)}>
              <CachedOutlined />
            </IconButton>
          </Tooltip>
        </FormControl>
        <FormControl sx={{ m: 1, mt: 3 }}>
          <Tooltip title={"Informação da Empresa"}>
            <span>
              <IconButton disabled={empresa == null} variant='outlined' onClick={() => {
                api.get(`/empresa/find/cod_empresa/${empresa.cod_empresa}`).then(res => {
                  setId(res.data.id);
                  setHandlerDialogEmpresa(true);
                }).catch(err => {
                  const msg = err?.response?.status === 404
                    ? 'Empresa não cadastrada no Horus'
                    : 'Erro ao buscar empresa'
                  alert(msg);
                });
              }}>
                <Info />
              </IconButton>
            </span>
          </Tooltip>
        </FormControl>
      </Box>

      <Backdrop
        sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }}
        open={openBackDrop}
      >
        <CircularProgress color="inherit" />
      </Backdrop>
      <HandleMenuContext>
        <FormDialog open={handlerDialogEmpresa} codEmpresa={empresa?.codEmpresa} id={id} handlerClose={() => setHandlerDialogEmpresa(false)} />
        <HostItemsDialog
          open={hostItemsDialog.open}
          codEmpresa={empresa?.cod_empresa}
          hostid={hostItemsDialog.hostid}
          hostname={hostItemsDialog.hostname}
          onClose={() => setHostItemsDialog({ open: false, hostid: null, hostname: '' })}
        />
        <ZabbixMap map={map} hosts={hosts} host={host} items={items} imgs={imgs} problems={problems} cod_empresa={empresa?.cod_empresa}
          onHostClick={(hostid, hostname) => {
            setHostItemsDialog({ open: true, hostid, hostname });
          }}
          onLoadSubMap={(subMapId) => {
            if (!empresa) return;
            const found = maplist.find(m => m.sysmapid == subMapId);
            if (found) {
              setSysmapid(found);
              loadMap(empresa.cod_empresa, found.sysmapid, found.name);
            } else {
              loadMap(empresa.cod_empresa, subMapId, `Mapa ${subMapId}`);
            }
          }}
        />
      </HandleMenuContext>
    </>
  );
}

export default memo(App);
